import { useEffect, useState } from "react";

import { flattenSchemaOptions } from "../../Utils/eventSchemaUtils";

import type { EventSchemaOptionEntry, OptionsData } from "../../eventSchemaTypes";

export type UseEventSchemaForTypeaheadParams = {
    /** Schema used to generate the flattened options for the typeahead. */
    eventSchema: EventSchemaOptionEntry | undefined;
    /** Hides the "Data Type" label rendered on each option when true. */
    hideTypeLabel?: boolean;
    /** Uses each field's `fieldId` (instead of its key) as the option value when true. */
    useFieldId?: boolean;
    /**
     * Selection change handler. Only used to reset the selected value to `undefined`
     * when the search input is cleared, mirroring the legacy component behavior.
     */
    onChange?: (evt: any) => void;
};

export type UseEventSchemaForTypeaheadResult = {
    /** Current flattened, search-highlighted options for the select. */
    options: OptionsData;
    /** Regenerates/filters options as the user types; clears selection when emptied. */
    onSearch: (searchValue: string) => void;
    /** Resets options back to the full list when the dropdown closes. */
    onDropdownVisibleChange: (open: boolean) => void;
};

/**
 * Flattens an {@link EventSchemaOptionEntry} into searchable, highlight-aware options and
 * returns the handlers needed to drive a presentational `RVTypeaheadSelect`.
 *
 * Spread the result onto `RVTypeaheadSelect`; the caller owns `value`, `placeholder`, and
 * (in forms) the injected `onChange`.
 *
 * @param params - {@link UseEventSchemaForTypeaheadParams}.
 * @returns {@link UseEventSchemaForTypeaheadResult} props for `RVTypeaheadSelect`.
 *
 * @example
 * const typeaheadProps = useEventSchemaForTypeahead({ eventSchema, useFieldId });
 * return <RVTypeaheadSelect {...typeaheadProps} placeholder="Attribute" />;
 */
export function useEventSchemaForTypeahead({
    eventSchema,
    hideTypeLabel = false,
    useFieldId = false,
    onChange,
}: UseEventSchemaForTypeaheadParams): UseEventSchemaForTypeaheadResult {
    const [options, setOptions] = useState<OptionsData>(() =>
        flattenSchemaOptions(eventSchema, "", hideTypeLabel, useFieldId)
    );

    useEffect(() => {
        setOptions(flattenSchemaOptions(eventSchema, "", hideTypeLabel, useFieldId));
    }, [eventSchema, hideTypeLabel, useFieldId]);

    function onSearch(searchValue: string) {
        if (!searchValue) {
            setOptions(flattenSchemaOptions(eventSchema, "", hideTypeLabel, useFieldId));
            onChange?.(undefined);
        } else {
            setOptions(flattenSchemaOptions(eventSchema, searchValue, hideTypeLabel, useFieldId));
        }
    }

    function onDropdownVisibleChange(open: boolean) {
        if (!open) {
            setOptions(flattenSchemaOptions(eventSchema, "", hideTypeLabel, useFieldId));
        }
    }

    return { options, onSearch, onDropdownVisibleChange };
}
